import { supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

export interface DeleteUserResult {
  user_id: string;
  deleted_stores_count: number;
  deleted_store_admins_count: number;
  deleted_at: string;
}

export class AuthService {
  static async signUp(email: string, password: string, userNumber: string): Promise<User | null> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          user_number: userNumber,
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    return data.user;
  }

  static async signIn(email: string, password: string): Promise<User> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(error.message);
    }

    return data.user;
  }

  static async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
  }

  // Fast auth user retrieval using session
  static async getAuthUser(): Promise<User | null> {
    try {
      // Use getSession() instead of getUser() - much faster
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        logger.warn('Session check error', { error: error.message, component: 'AuthService' });
        return null;
      }
      return session?.user || null;
    } catch (error) {
      logger.warn('Failed to get session', { error, component: 'AuthService' });
      return null;
    }
  }

  static async resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) {
      throw new Error(error.message);
    }
  }

  static async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) {
      throw new Error(error.message);
    }
  }

  static onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user ?? null);
    });
  }

  // Phone OTP login: send OTP to phone
  static async signInWithPhoneOtp(phone: string): Promise<{ data: any; error: any }> {
    // Special handling for fixed OTP number
    const cleanPhone = phone.replace(/\D/g, ''); // Remove all non-digits
    const normalizedPhone = cleanPhone.startsWith('91') && cleanPhone.length === 12
      ? cleanPhone.slice(2)
      : cleanPhone;

    const TEST_PHONES = ['7878787878', '9000000001', '9000000002'];
    if (TEST_PHONES.includes(normalizedPhone)) {
      // For test numbers, return success without actually sending OTP
      logger.info('Using fixed OTP for test number', { phone: normalizedPhone });
      return { data: { success: true }, error: null };
    }

    // phone should be in E.164 format, e.g. '+911234567890'
    const phoneWithCountryCode = phone.startsWith('+') ? phone : `+91${phone}`;
    return await supabase.auth.signInWithOtp(
      {
        phone: phoneWithCountryCode,
        options: {
          channel: 'whatsapp',
          shouldCreateUser: true,
          data: {
            user_number: phone,
          }
        },
      }
    );
  }

  // Phone OTP verification: verify the OTP sent to phone
  static async verifyPhoneOtp(phone: string, token: string): Promise<{ user: any; session: any; error: any }> {
    // Special handling for fixed OTP number - use password-based authentication
    const cleanPhone = phone.replace(/\D/g, ''); // Remove all non-digits
    const normalizedPhone = cleanPhone.startsWith('91') && cleanPhone.length === 12
      ? cleanPhone.slice(2)
      : cleanPhone;

    const TEST_PHONES = ['7878787878', '9000000001', '9000000002'];
    if (TEST_PHONES.includes(normalizedPhone)) {
      logger.info('Using password-based auth for test number', { phone: normalizedPhone });

      try {
        // Use the OTP as password for the phone-based email
        const specialEmail = `${normalizedPhone}@kirana360.in`;
        const user = await this.signIn(specialEmail, token);

        // Get the session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          logger.error('Error getting session for fixed OTP user', { error: sessionError });
          return { user: null, session: null, error: { message: 'Session error' } };
        }

        logger.info('Fixed OTP user authenticated successfully', { userId: user.id, phone: normalizedPhone });
        return { user, session, error: null };
      } catch (err: any) {
        logger.warn('Fixed OTP authentication failed', { phone: normalizedPhone, error: err.message });
        return { user: null, session: null, error: { message: 'Invalid OTP' } };
      }
    }

    // type: 'sms' for normal login
    const { data, error } = await supabase.auth.verifyOtp({ phone, token, type: 'sms' });
    return { user: data.user, session: data.session, error };
  }

  // Fetch public user row by auth user id (uid)
  static async getPublicUserByAuthId(authUserId: string): Promise<any | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*, is_superadmin')
      .eq('uid', authUserId)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null; // No rows found
    }
    return data || null;
  }

  /**
   * Update user profile in public.users table
   * @param authUserId - The UUID of the authenticated user
   * @param updates - Object with fields to update (name, name_locale, etc.)
   * @returns Promise with updated user data
   */
  static async updateUserProfile(authUserId: string, updates: { name?: string; name_locale?: string; address?: string; address_locale?: string }): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('uid', authUserId)
        .select('*, is_superadmin')
        .single();

      if (error) {
        logger.error('Failed to update user profile', { error, authUserId });
        throw new Error(`Failed to update profile: ${error.message}`);
      }

      return data || null;
    } catch (error) {
      logger.error('Error updating user profile', { error, authUserId });
      throw error instanceof Error ? error : new Error('Failed to update user profile');
    }
  }

  /**
   * Permanently delete a user and all their related data
   * This will permanently delete the user, their stores (using delete_store_data), and store_admins records
   * @param userId - The UUID of the user to delete
   * @returns Promise with deletion summary
   */
  static async deleteUser(userId: string): Promise<DeleteUserResult> {
    try {
      logger.info('Initiating user deletion', { userId });

      if (!userId) {
        throw new Error('User ID is required');
      }

      // Call the delete_user_data database function
      const { data, error } = await supabase
        .rpc('delete_user_data', { user_id_to_delete: userId });

      if (error) {
        logger.error('Failed to delete user', { error, userId });
        throw new Error(`Failed to delete user: ${error.message}`);
      }

      if (!data) {
        throw new Error('No response received from delete operation');
      }

      logger.info('User deleted successfully', { userId, result: data });
      return data as DeleteUserResult;
    } catch (error) {
      logger.error('Error in user deletion', { error, userId });
      throw error instanceof Error ? error : new Error('Unknown error occurred during user deletion');
    }
  }

  /**
   * Get current user profile (both auth and public data)
   * @returns Promise with user profile data
   */
  static async getCurrentUserProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Get the public user data
      const { data: publicUser, error } = await supabase
        .from('users')
        .select('*')
        .eq('uid', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('User profile not found');
        }
        throw error;
      }

      return {
        authUser: user,
        publicUser
      };
    } catch (error) {
      logger.error('Error getting current user profile', { error });
      throw error instanceof Error ? error : new Error('Failed to get current user profile');
    }
  }

  /**
   * Check if a user exists
   * @param userId - The UUID of the user to check
   * @returns Promise<boolean> - true if user exists
   */
  static async userExists(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('uid')
        .eq('uid', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return false; // User not found
        }
        throw error;
      }

      return Boolean(data);
    } catch (error) {
      logger.error('Error checking user existence', { error, userId });
      throw error instanceof Error ? error : new Error('Failed to check user existence');
    }
  }
}